import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';

import { FormQuestions } from '@form';

import { Radios } from 'nhsuk-react-components';

const Radio = forwardRef((props, ref) => {
    const [error, setError] = useState(undefined);
    const [isValid, setIsValid] = useState(undefined);

    useImperativeHandle(ref, () => ({
        validate,
        isValid
    }));

    const validate = value => {
        const validateValue = value ? value : props.question.value;
        if (props.question.mandatory) {
            if (!validateValue) {
                setError('Please make a selection');
                setIsValid(false);
            } else {
                setError(undefined);
                setIsValid(true);
            }
        } else {
            setError(undefined);
            setIsValid(true);
        }
    };

    const normaliseQuestionRefs = questionRefs => {
        if (!questionRefs) {
            return [];
        }

        return Array.isArray(questionRefs) ? questionRefs : [questionRefs];
    };

    const getQuestionIds = questionRefs => {
        return normaliseQuestionRefs(questionRefs)
            .map(question => (typeof question === 'string' ? question : question?.id))
            .filter(Boolean);
    };

    const getOptionChildIds = option => {
        if (option?.hide_linked_question === false && !option?.linked_question_id) {
            return getQuestionIds(props.question.linked_questions);
        }

        return getQuestionIds([
            ...(option?.questions || []),
            ...(option?.linked_question_id
                ? Array.isArray(option.linked_question_id)
                    ? option.linked_question_id
                    : [option.linked_question_id]
                : [])
        ]);
    };

    const toggleConditionalRevealQuestions = value => {
        if (!props.onConditionalReveal) {
            return;
        }

        const allChildIds = props.question.options.flatMap(getOptionChildIds);

        if (!value) {
            props.onConditionalReveal({
                isRevealed: false,
                questionIds: allChildIds
            });

            return;
        }

        const selectedOption = props.question.options.find(option => option.key === value);

        const revealIds = new Set(getOptionChildIds(selectedOption));

        props.onConditionalReveal({
            isRevealed: false,
            questionIds: allChildIds.filter(id => !revealIds.has(id))
        });

        props.onConditionalReveal({
            isRevealed: true,
            questionIds: Array.from(revealIds)
        });
    };

    const handleChange = async event => {
        const value = event.target.value;

        await props.onChange({
            questionId: props.question.id,
            linked_questions: props.question.linked_questions || null,
            option: props.question.options
                ? props.question.options.find(x => x.key === value)
                : null,
            value
        });

        validate(value);

        if (props.onConditionalReveal && event.type === 'change') {
            toggleConditionalRevealQuestions(value);
        }
    };

    useEffect(() => {
        if (
            props.onConditionalReveal &&
            props.question.value !== undefined &&
            props.question.value !== null
        ) {
            toggleConditionalRevealQuestions(props.question.value);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.question.value]);

    const value = props.question.value ?? null;
    const { mandatory } = props.question;

    const shouldShow = props.question.hidden !== true;

    return shouldShow ? (
        <Radios
            inline={props.question.display === 'inline'}
            label={props.question.name}
            hint={props.question.hint_text}
            value={value}
            error={error}
        >
            {props.question.options.map((option, index) => (
                <Radios.Radio
                    key={index}
                    value={option.key}
                    checked={option.key === value}
                    onChange={handleChange}
                    hint={option.hint_text}
                    required={mandatory}
                    conditional={
                        option.questions
                            ? option.questions.map((question, index) => {
                                  const linkedQuestion = props.questions.find(
                                      x => x.id === question.id
                                  );

                                  return (
                                      <FormQuestions
                                          question={linkedQuestion}
                                          questions={props.questions}
                                          key={index}
                                          onQuestionChange={props.onChange}
                                          onConditionalReveal={props.onConditionalReveal}
                                          handleDisable={props.handleDisable}
                                          enteredDetails={props.enteredDetails}
                                          onHcpcValidationChange={props.onHcpcValidationChange}
                                          gmcValue={props.gmcValue}
                                          hcpcValue={props.hcpcValue}
                                          skipHcpcPersonValidation={props.skipHcpcPersonValidation}
                                          ref={linkedQuestion?.ref}
                                      />
                                  );
                              })
                            : undefined
                    }
                >
                    {option.value}
                </Radios.Radio>
            ))}
        </Radios>
    ) : null;
});

export default Radio;
